import type { ComponentRef } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { View } from "react-native";
import { Button, ButtonSpinner, ButtonText } from "../../../../ui/button";
import { HStack } from "../../../../ui/hstack";
import { Text } from "../../../../ui/text";
import { VStack } from "../../../../ui/vstack";
import { optionsKey } from "../../formLiveReducer";
import { useAgentForm } from "./AgentFormContext";
import FormCardFrame from "./FormCardFrame";
import FormField from "./FormField";
import type {
  FormCard,
  FormErrors,
  FormFieldSpec,
  FormValue,
  FormValues,
} from "./formTypes";
import type { OptionsRequest } from "./formValues";
import {
  firstInvalidKey,
  groupRows,
  initialValues,
  isFieldVisible,
  isPlausibleUrl,
  productChoice,
  resolveOptions,
  routeActionErrors,
  toSubmitValues,
  validateForm,
} from "./formValues";

/** Past this, the options are not coming; the skeleton goes away without a word. */
const OPTIONS_TIMEOUT_MS = 15_000;
/** How long typing settles before the draft is written. */
const DRAFT_SAVE_DEBOUNCE_MS = 400;

type Request = { token: number; request: OptionsRequest };

type Props = {
  card: FormCard;
  /**
   * What the card opens with: what was submitted when re-opening, otherwise the saved
   * draft. Read once, which is why the card is only mounted after the draft has loaded.
   */
  initial: FormValues;
  /** Present when re-opening; drops the edit and returns to the summary. */
  onCancel?: () => void;
  /** Keep the unsubmitted values on the device; a no-op when re-opening. */
  saveDraft: (values: FormValues) => void;
  clearDraft: () => void;
};

const without = <T,>(record: Record<string, T>, key: string) =>
  Object.fromEntries(Object.entries(record).filter(([name]) => name !== key));

/**
 * The live card: every field of the Brief or the Logistics form, validated as a whole
 * and sent with one submit. `FormAnswer` loads the saved draft first and mounts this.
 *
 * The card owns the values; each control just draws one. What it adds on top of that:
 *
 *  - **Drafts.** Unsubmitted values are saved as they change, so nothing typed is lost
 *    to a reload.
 *  - **Late options.** A select whose options are computed after the card is shown (the
 *    purchase driver) is asked for the moment its product is chosen — see
 *    `onCommitProduct` — and shown as a skeleton until they arrive, or quietly not at
 *    all if they never do. A product restored from a draft asks once, on mount.
 *  - **Validation on submit.** Nothing is flagged while the person is still filling the
 *    card in; the first refused submit flags every field and moves focus to the first.
 *    The backend's own `userErrors` land next to the field they name.
 *
 * Re-opened (`onCancel` set), it is the Brief edit: same card, prefilled, with a
 * confirmation first because submitting replaces the concepts.
 */
const FormEditor = ({
  card,
  initial,
  onCancel,
  saveDraft,
  clearDraft,
}: Props) => {
  const { live, busy, waitingForScript, submitForm, requestFormOptions } =
    useAgentForm();
  const editing = !!onCancel;

  const [values, setValues] = useState<FormValues>(() =>
    initialValues(card.fields, initial),
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // --- drafts ---------------------------------------------------------------
  const touched = useRef(false);

  useEffect(() => {
    if (editing || !touched.current) return;
    const timer = setTimeout(() => saveDraft(values), DRAFT_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [values, editing, saveDraft]);

  // --- late options ---------------------------------------------------------
  const dynamicFields = useMemo(
    () => card.fields.filter((field) => field.optionsStatus !== "ready"),
    [card.fields],
  );
  const [requests, setRequests] = useState<Record<string, Request>>({});
  const tokenRef = useRef(0);
  const lastRequested = useRef<string | null>(null);
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const liveRef = useRef(live);
  liveRef.current = live;

  useEffect(
    () => () => Object.values(timers.current).forEach(clearTimeout),
    [],
  );

  const patchRequest = useCallback(
    (key: string, token: number, patch: Partial<OptionsRequest>) =>
      setRequests((current) => {
        const existing = current[key];
        if (!existing || existing.token !== token) return current;
        return {
          ...current,
          [key]: { token, request: { ...existing.request, ...patch } },
        };
      }),
    [],
  );

  const askForOptions = useCallback(
    (
      field: FormFieldSpec,
      product: { productId: string } | { url: string },
      keepValue: boolean,
    ) => {
      tokenRef.current += 1;
      const token = tokenRef.current;
      clearTimeout(timers.current[field.key]);
      setRequests((current) => ({
        ...current,
        [field.key]: {
          token,
          request: {
            seqAtRequest:
              liveRef.current.options[optionsKey(card.formId, field.key)]
                ?.seq ?? 0,
            productId: "productId" in product ? product.productId : null,
            url: "url" in product ? product.url : null,
            failed: false,
            timedOut: false,
          },
        },
      }));
      // What they picked was a pick for the previous product.
      if (!keepValue) setValues((current) => without(current, field.key));
      timers.current[field.key] = setTimeout(
        () => patchRequest(field.key, token, { timedOut: true }),
        OPTIONS_TIMEOUT_MS,
      );
      void requestFormOptions(product).then((ok) => {
        if (!ok) patchRequest(field.key, token, { failed: true });
      });
    },
    [card.formId, requestFormOptions, patchRequest],
  );

  const onCommitProduct = useCallback(
    (product: FormValue, keepValues = false) => {
      if (!dynamicFields.length) return;
      const choice = productChoice(product);
      if (choice.kind === "none") return;
      // A link only counts once it could be one; a typo must not spend a model call.
      if (choice.kind === "link" && !isPlausibleUrl(choice.url)) return;
      // Options depend on the product alone: a price typed beside a link is not part of it.
      const signature = JSON.stringify(
        choice.kind === "link"
          ? { kind: choice.kind, url: choice.url }
          : choice,
      );
      if (signature === lastRequested.current) return;
      lastRequested.current = signature;
      const target =
        choice.kind === "catalogue"
          ? { productId: choice.productId }
          : { url: choice.url };
      dynamicFields.forEach((field) =>
        askForOptions(field, target, keepValues),
      );
    },
    [dynamicFields, askForOptions],
  );

  // A product restored from a draft never went through a pick, so nothing has asked
  // for the options that depend on it. Ask once, and keep what was restored.
  useEffect(() => {
    if (editing) return;
    const productField = card.fields.find((field) => field.kind === "product");
    const restored = productField ? values[productField.key] : undefined;
    if (restored !== undefined) onCommitProduct(restored, true);
    // Mount only: later picks reach `onCommitProduct` through the field itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- editing --------------------------------------------------------------
  const fieldRefs = useRef(new Map<string, ComponentRef<typeof View>>());
  const register = useCallback(
    (key: string, node: ComponentRef<typeof View> | null) => {
      if (node) fieldRefs.current.set(key, node);
      else fieldRefs.current.delete(key);
    },
    [],
  );

  const setField = useCallback((key: string, value: FormValue | undefined) => {
    touched.current = true;
    setConfirming(false);
    setValues((current) =>
      value === undefined
        ? without(current, key)
        : { ...current, [key]: value },
    );
    setErrors((current) => (current[key] ? without(current, key) : current));
  }, []);

  const validateOne = useCallback(
    (field: FormFieldSpec) => {
      const message = validateForm([field], values)[field.key];
      setErrors((current) => {
        if (message) return { ...current, [field.key]: message };
        return current[field.key] ? without(current, field.key) : current;
      });
    },
    [values],
  );

  const focusFirst = (found: FormErrors) => {
    const key = firstInvalidKey(card.fields, found);
    if (key) fieldRefs.current.get(key)?.focus();
  };

  // --- submit ---------------------------------------------------------------
  const submit = async () => {
    const found = validateForm(card.fields, values);
    setErrors(found);
    setFormError(null);
    if (Object.keys(found).length) {
      focusFirst(found);
      return;
    }
    if (editing && !confirming) {
      setConfirming(true);
      return;
    }

    setSubmitting(true);
    const result = await submitForm(
      card.action,
      toSubmitValues(card.fields, values),
    );
    setSubmitting(false);
    if (result.ok) {
      clearDraft();
      return;
    }

    const { fieldErrors: fromServer, general } = routeActionErrors(
      card.fields,
      values,
      result.errors,
    );
    setConfirming(false);
    setErrors(fromServer);
    setFormError(general);
    focusFirst(fromServer);
  };

  const waiting = card.formId === "LOGISTICS" && waitingForScript;
  const blocked = submitting || waiting || (editing && busy);
  const label = waiting
    ? "Esperando el guion…"
    : confirming
      ? "Sí, generar nuevos conceptos"
      : card.submitLabel;

  const rows = useMemo(
    () =>
      groupRows(card.fields.filter((field) => isFieldVisible(field, values))),
    [card.fields, values],
  );

  const control = (field: FormFieldSpec) => {
    const request = requests[field.key]?.request ?? null;
    return (
      <FormField
        key={field.key}
        field={field}
        value={values[field.key]}
        values={values}
        onChange={(value) => setField(field.key, value)}
        disabled={submitting}
        error={errors[field.key]}
        options={resolveOptions(
          field,
          live.options[optionsKey(card.formId, field.key)],
          request,
        )}
        onCommitProduct={onCommitProduct}
        onBlur={() => {
          // Only the pasted link is worth flagging before submit: a required-but-empty
          // field is not wrong yet, the person has not got to it.
          if (
            field.kind === "product" &&
            productChoice(values[field.key]).kind === "link"
          ) {
            validateOne(field);
          }
        }}
        register={register}
      />
    );
  };

  return (
    <FormCardFrame label={card.prompt}>
      <VStack space="lg">
        <Text className="text-sm font-semibold text-typography-900">
          {card.prompt}
        </Text>

        {rows.map((row) => {
          const [only] = row;
          if (row.length === 1 && only) return control(only);
          return (
            <HStack
              key={row.map((field) => field.key).join("+")}
              space="md"
              className="items-start"
            >
              {row.map(control)}
            </HStack>
          );
        })}

        {formError ? (
          <Text role="alert" className="text-sm text-error-600">
            {formError}
          </Text>
        ) : null}

        {confirming ? (
          <Text
            accessibilityLiveRegion="polite"
            className="text-sm text-typography-700"
          >
            Esto generará nuevos conceptos. Los actuales quedarán marcados como
            basados en un brief anterior.
          </Text>
        ) : null}

        <HStack space="sm" className="items-center justify-end">
          {onCancel ? (
            <Button
              action="muted"
              variant="link"
              size="md"
              className="min-h-11"
              isDisabled={submitting}
              onPress={confirming ? () => setConfirming(false) : onCancel}
            >
              <ButtonText>{confirming ? "Volver" : "Cancelar"}</ButtonText>
            </Button>
          ) : null}
          <Button
            action="brand"
            variant="solid"
            size="md"
            className="min-h-11"
            isDisabled={blocked}
            onPress={() => void submit()}
          >
            {submitting ? <ButtonSpinner /> : null}
            <ButtonText>{label}</ButtonText>
          </Button>
        </HStack>
        {waiting ? (
          <Text className="text-right text-xs text-typography-500">
            Puedes ir completándolo; se habilita cuando el guion esté listo.
          </Text>
        ) : null}
      </VStack>
    </FormCardFrame>
  );
};

export default FormEditor;
